import { createTheme, darken, lighten } from '@mui/material';
import { useAtomValue } from 'jotai';

import { drawerWidth } from '../common/models/utils/constants';
import { themeAtom } from './themeAtom';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    menu: true;
  }
  interface ButtonPropsColorOverrides {
    toggle: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    appbar: true;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    custom: Record<string, any>;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    custom?: Record<string, any>;
  }

  interface Palette {
    appbar: Palette['primary'];
    menu: Palette['primary'];
    toggle: Palette['primary'];
  }
  interface PaletteOptions {
    appbar: PaletteOptions['primary'];
    menu: PaletteOptions['primary'];
    toggle: PaletteOptions['primary'];
  }
}

const colors = {
  primary: {
    dark: '#52796F',
    light: '#19615C',
  },
  menuButton: {
    main: {
      dark: '#233031',
      light: '#FFFFFF',
    },
    contrastText: {
      dark: '#ABBEC4',
      light: '#201F1E',
    },
  },
  toggleButton: {
    main: {
      dark: '#233031',
      light: '#FFFFFF',
    },
    contrastText: {
      dark: '#ABBEC4',
      light: '#201F1E',
    },
    border: {
      dark: '#233031',
      light: '#E7E7E9',
    },
  },
  appBar: {
    background: {
      dark: '#233031',
      light: '#E2E5E6',
    },
    icon: {
      dark: '#ABBEC4',
      light: '#31363A',
    },
  },
  dashboard: {
    background: {
      dark: '#0C0F0D',
      light: '#E2E5E6',
    },
    paper: {
      dark: '#121919',
      light: '#FFFFFF',
    },
  },
  nav: {
    color: {
      dark: '#ABBEC4',
      light: '#6B7278',
    },
    background: {
      dark: '#121919',
      light: '#FFFFFF',
    },
    buttonSelected: {
      dark: '#52796F',
      light: '#19615C',
    },
    buttonHover: {
      dark: darken('#52796F', 0.7),
      light: lighten('#19615C', 0.9),
    },
    buttonSelectedHover: {
      dark: darken('#52796F', 0.2),
      light: lighten('#19615C', 0.2),
    },
  },
};

const useTheme = () => {
  const mode = useAtomValue(themeAtom);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary[mode],
      },
      menu: {
        main: colors.menuButton.main[mode],
        contrastText: colors.menuButton.contrastText[mode],
      },
      toggle: {
        main: colors.toggleButton.main[mode],
        contrastText: colors.toggleButton.contrastText[mode],
      },
      background: {
        default: colors.dashboard.background[mode],
        paper: colors.dashboard.paper[mode],
      },
      appbar: {
        main: colors.appBar.icon[mode],
      },
    },
    typography: {
      fontFamily: `'Montserrat', 'Roboto', 'Helvetica', 'Arial', sans-serif`,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 500 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 500 },
      subtitle1: { fontWeight: 700 },
      subtitle2: { fontWeight: 500 },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: colors.appBar.background[mode],
            boxShadow: 'none',
            borderRadius: 0,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            backgroundColor: colors.dashboard.background[mode],
          },
          paper: {
            background: colors.menuButton.main[mode],
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.appBar.icon[mode],
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { color: 'menu' },
            style: {
              textTransform: 'none',
            },
          },
          {
            props: { color: 'toggle' },
            style: {
              border: `1px solid transparent`,
              color: colors.toggleButton.contrastText[mode],
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
          {
            props: { variant: 'contained', color: 'toggle' },
            style: {
              border: `1px solid ${colors.toggleButton.border[mode]}`,
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },

          {
            props: { variant: 'contained', size: 'medium' },
            style: {
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
          {
            props: { variant: 'text', size: 'medium' },
            style: {
              paddingTop: 5,
              paddingBottom: 5,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
          {
            props: { variant: 'text', size: 'small' },
            style: {
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 10,
              paddingRight: 10,
              minWidth: 0,
            },
          },
        ],
        styleOverrides: {
          root: {
            boxShadow: 'none',
            fontSize: '0.875em',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 6,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor:
              mode === 'dark'
                ? lighten(colors.nav.background[mode], 0.075)
                : colors.nav.background[mode],
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.nav.background[mode],
            backgroundImage: 'none',
            boxSizing: 'border-box',
            width: drawerWidth,
            border: 0,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.primary.light,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: colors.nav.color[mode],
            marginBottom: 4,
            '&:hover': {
              backgroundColor: colors.nav.buttonHover[mode],
            },
            '&.Mui-selected': {
              color: '#FFFFFF',
              backgroundColor: colors.nav.buttonSelected[mode],
              '> .MuiListItemIcon-root': {
                color: '#FFFFFF',
              },
            },
            '&.Mui-selected:hover': {
              color: '#FFFFFF',
              backgroundColor: colors.nav.buttonSelectedHover[mode],
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: colors.nav.color[mode],
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            color: colors.primary[mode],
            background: 'transparent',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            background: colors.menuButton.main[mode],
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875em',
            '&$selected': {},
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            // background: colors.dashboard.background[mode],
            fontSize: '0.875em',
            '&.MuiOutlinedInput-input': {
              // paddingTop: 4,
              // paddingBottom: 4,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minWidth: '12.5%',
            textTransform: 'none',
            '&.Mui-selected': {
              color: 'inherit',
              backgroundColor:
                mode === 'dark'
                  ? lighten(colors.appBar.background.dark, 0.1)
                  : lighten(colors.appBar.background.light, 0.5),
            },
            '&.Mui-disabled': {
              fontStyle: 'italic',
              backgroundColor:
                mode === 'dark'
                  ? lighten(colors.appBar.background.dark, 0.0025)
                  : lighten(colors.appBar.background.light, 0.8),
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              fontWeight: 700,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom:
              mode === 'dark'
                ? `1px solid ${lighten(colors.nav.background.dark, 0.2)}`
                : `1px solid ${darken(colors.nav.buttonHover.light, 0.1)}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            columnGap: 10,
            minWidth: 360,
          },
        },
      },
    },
    custom: {
      colors: colors,
      buttonGroupContainer: {
        display: 'inline-block',
        borderRadius: 2,
        pt: 0.4,
        pl: 0.4,
        pb: 0.4,
        pr: 0.4,
        mb: 2,
        backgroundColor:
          mode === 'dark'
            ? colors.nav.background.dark
            : colors.nav.buttonHover.light,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor:
          mode === 'dark'
            ? lighten(colors.nav.background.dark, 0.05)
            : darken(colors.nav.buttonHover.light, 0.1),
      },
      contentBox: {
        border:
          mode === 'dark'
            ? `1px solid ${lighten(colors.nav.background.dark, 0.2)}`
            : `1px solid ${darken(colors.nav.buttonHover.light, 0.1)}`,
        borderRadius: 1.5,
        p: 0.5,
        pl: 2,
        pr: 1,
        '&.selected': {
          background:
            mode === 'dark'
              ? lighten(colors.nav.background.dark, 0.02)
              : lighten(colors.nav.buttonHover.light, 0.5),
        },
      },
      fileRemove: {
        backgroundColor:
          mode === 'dark'
            ? colors.nav.background.dark
            : lighten(colors.appBar.background.light, 0.75),
      },
      fileUpload: {
        border:
          mode === 'dark'
            ? `1px dashed ${lighten(colors.nav.background.dark, 0.5)}`
            : `2px dashed ${colors.nav.buttonHover.light}`,
      },
      legendItem: {
        backgroundColor:
          mode === 'dark'
            ? colors.nav.background.dark
            : colors.nav.buttonHover.light,
      },
      lineItem: {
        borderBottom:
          mode === 'dark'
            ? `1px solid ${lighten(colors.nav.background.dark, 0.2)}`
            : `1px solid ${darken(colors.nav.buttonHover.light, 0.1)}`,
      },
      roundInput: {
        background:
          mode === 'dark' ? colors.dashboard.background.dark : '#FFFFFF',
        border:
          mode === 'dark'
            ? `1px solid transparent`
            : `1px solid ${colors.appBar.background.light}`,
      },
      tableHeaderRow: {
        background: mode === 'dark' ? '#202424' : '#FFFFFF',
      },
      tableRowSelected: {
        background:
          mode === 'dark'
            ? darken(colors.primary.dark, 0.5)
            : lighten(colors.primary.light, 0.9),
        borderBottom:
          mode === 'dark'
            ? `1px solid ${colors.primary.dark}`
            : `1px solid ${colors.primary.light}`,
        borderTop:
          mode === 'dark'
            ? `1px solid ${colors.primary.dark}`
            : `1px solid ${colors.primary.light}`,
      },
      toolbarHeight: {
        height: 110,
        paddingBottom: 3.5,
        paddingTop: 1,
        flexWrap: 'wrap',
        rowGap: 1,
      },
    },
  });
};

export default useTheme;
